import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useNavigate, useParams } from 'react-router-dom-v5-compat';

import { Loader } from 'components/Loader';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import {
  locationReadScopes,
  msiAltaIntegrationReadScopes,
  redFlagReadScopes,
  userLocationsReadScopes,
  visitorScopeTemplates,
} from 'constants/visitorScopes';

import { visitorWelcomeRoute } from './constants/routes';
import i18n from './i18n/i18n';
import {
  describeLocation,
  describeLocationWorkflow,
  fetchLocations,
  clearLocationsState,
} from './store/slices/locationsSlice';
import {
  fetchIntegrationStatus,
  clearIntegrationState,
} from './store/slices/integrationSlice';
import {
  fetchSelectedUserLocations,
  clearUsersState,
} from './store/slices/usersSlice';
import { fetchRedFlags, clearRedFlagState } from './store/slices/redFlagSlice';
import { clearAgreementsState } from './store/slices/agreementsSlice';
import { clearDenyState } from './store/slices/denySlice';
import { clearEmailTemplateState } from './store/slices/emailTemplatesSlice';
import { clearKiosksState } from './store/slices/kiosksSlice';
import { clearVisitorInvitationState } from './store/slices/visitorInvitationSlice';
import { clearVisitorSearchState } from './store/slices/visitorSearchSlice';
import { clearVisitorsState } from './store/slices/visitorsSlice';
import { clearVisitorTypesState } from './store/slices/visitorTypesSlice';
import { clearVisitorWorkflowsState } from './store/slices/visitorWorkflowsSlice';
import { clearVisitsState } from './store/slices/visitsSlice';
import { ImmutableRootState } from './store/store';

export const VisitorDataLoader = () => {
  const dispatch = useDispatch();

  const { orgId: reactiveOrgId } = useParams();
  const orgId = Number(reactiveOrgId);

  const navigate = useNavigate();

  const { userId, identity, isLoading } = useIdentityAndUser();

  // Sync i18n language with the user's saved language preference from the
  // backend. This propagates language changes made in the
  // Control app's Profile Drawer, since visitorApp is a separate app on a
  // different subdomain and cannot rely on shared localStorage.
  useEffect(() => {
    if (identity?.language && identity.language !== i18n.language) {
      i18n.changeLanguage(identity.language);
    }
  }, [identity?.language]);

  const globalLocation = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation']),
  );
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  );
  const fetchLocationsLoading = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'fetchLocationsLoading']),
  ) as boolean;
  const selectedUserLocations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['users', 'selectedUserLocations']),
  );

  // Check if user has any visitor scope to prevent unnecessary API calls
  // This matches mailroom's behavior where each page is protected by OpScopeGuard
  const { isValid: hasAnyVisitorScope } = useValidateScopes([
    ...visitorScopeTemplates,
    'o:r',
    'o:w',
    's-o:r',
    's-o:w',
  ]);

  // Only users who can read locations should block the app on location load.
  // Users without this scope get their locations request 403'd, so we must not
  // wait for `locations`/`globalLocation` to populate (that caused an infinite
  // "Loading Visitor..." spinner for limited-scope roles, e.g. dashboard-only).
  const { isValid: hasLocationRead } = useValidateScopes(locationReadScopes);

  // Only fetch the user's locations when the user has a scope helium's GET
  // /users/{userId}/locations route accepts (otherwise it 403s).
  const { isValid: hasUserReadWrite } = useValidateScopes(
    userLocationsReadScopes,
  );

  // Only fetch red flags when the user has a scope helium's GET /redflag route
  // accepts (otherwise it 403s with "method that get a redflags").
  const { isValid: hasRedFlagRead } = useValidateScopes(redFlagReadScopes);

  // Only fetch the MSI Alta integration status when the user has a scope
  // helium's GET /integrationMsiAlta route accepts (otherwise it 403s).
  const { isValid: hasMsiAltaIntegrationRead } = useValidateScopes(
    msiAltaIntegrationReadScopes,
  );

  // Detect unprovisioned orgs: if locations fetch completes with no results,
  // redirect to welcome page. Gate on `hasLocationRead`: without location read
  // the /locations request 403s and leaves `locations` empty, which is
  // indistinguishable from a genuinely unprovisioned org — limited-scope roles
  // (e.g. invitation-only) were wrongly redirected to the welcome page.
  const locationsFetchingRef = useRef(false);

  useEffect(() => {
    const wasLoading = locationsFetchingRef.current;
    locationsFetchingRef.current = fetchLocationsLoading;

    if (
      hasLocationRead &&
      wasLoading &&
      !fetchLocationsLoading &&
      Array.isArray(locations) &&
      locations.length === 0
    ) {
      navigate(`/o/${reactiveOrgId}/${visitorWelcomeRoute}`, { replace: true });
    }
  }, [
    hasLocationRead,
    fetchLocationsLoading,
    locations,
    navigate,
    reactiveOrgId,
  ]);

  // Tracks whether a locations fetch has at least started, so we can tell the
  // difference between "not fetched yet" (keep waiting) and "fetched but empty
  // / failed" (stop waiting) once the loading flag settles back to false.
  const [locationsFetchStarted, setLocationsFetchStarted] = useState(false);
  useEffect(() => {
    if (fetchLocationsLoading) {
      setLocationsFetchStarted(true);
    }
  }, [fetchLocationsLoading]);
  const locationsFetchDone = locationsFetchStarted && !fetchLocationsLoading;

  const clearOrgData = useCallback(() => {
    dispatch(clearAgreementsState());
    dispatch(clearDenyState());
    dispatch(clearKiosksState());
    dispatch(clearLocationsState());
    dispatch(clearRedFlagState());
    dispatch(clearEmailTemplateState());
    dispatch(clearVisitorInvitationState());
    dispatch(clearVisitorSearchState());
    dispatch(clearVisitorsState());
    dispatch(clearVisitsState());
    dispatch(clearVisitorTypesState());
    dispatch(clearVisitorWorkflowsState());
    dispatch(clearUsersState());
    dispatch(clearIntegrationState());
  }, [dispatch]);

  // Bootstrap (clear stale org data + fetch locations) exactly once per org.
  // Keying this off the async scope flags caused it to re-run whenever a flag
  // settled in a later render (React Query timing varies), each time clearing
  // `locations`/`globalLocation` and refetching — which cascaded into duplicate
  // describeLocation/describeLocationWorkflow calls. A ref makes it deterministic.
  const bootstrappedOrgRef = useRef<number | null>(null);
  useEffect(() => {
    // Only clear stale data if user has visitor access (like mailroom's
    // OpScopeGuard approach).
    if (!orgId || !hasAnyVisitorScope) return;
    if (bootstrappedOrgRef.current === orgId) return;
    bootstrappedOrgRef.current = orgId;

    clearOrgData();
  }, [orgId, hasAnyVisitorScope, clearOrgData]);

  // Fetch locations in its own once-per-org effect gated on the scopes helium's
  // GET /locations route accepts, kept separate from the bootstrap effect above
  // so it can't be skipped by that effect's ref guard. Without the gate, limited
  // roles 403 and surface a "Missing permission" toast.
  const locationsFetchedOrgRef = useRef<number | null>(null);
  useEffect(() => {
    if (!orgId || !hasAnyVisitorScope || !hasLocationRead) return;
    if (locationsFetchedOrgRef.current === orgId) return;
    locationsFetchedOrgRef.current = orgId;

    dispatch(fetchLocations({ orgId, status: 1 }));
  }, [orgId, hasAnyVisitorScope, hasLocationRead, dispatch]);

  useEffect(() => {
    if (orgId && hasAnyVisitorScope && hasRedFlagRead) {
      dispatch(fetchRedFlags({ orgId }));
    }
  }, [orgId, hasAnyVisitorScope, hasRedFlagRead, dispatch]);

  useEffect(() => {
    if (orgId && hasAnyVisitorScope && hasMsiAltaIntegrationRead) {
      dispatch(fetchIntegrationStatus({ orgId }));
    }
  }, [orgId, hasAnyVisitorScope, hasMsiAltaIntegrationRead, dispatch]);

  useEffect(() => {
    if (orgId && userId && hasUserReadWrite) {
      dispatch(fetchSelectedUserLocations({ orgId, userId }));
    }
  }, [orgId, userId, dispatch, hasUserReadWrite]);

  // Tracks the `locations` set we've already requested the global location for.
  // `describeLocation`/`describeLocationWorkflow` are async, so while they're
  // in-flight `globalLocation` stays null; without this guard every other
  // dependency settling (selectedUserLocations, hasUserReadWrite) would re-fire
  // the same describe calls, causing a burst of duplicate requests. Keying off
  // the `locations` reference also allows a fresh fetch (new reference) to
  // re-request, and avoids retry loops on error (reference stays the same).
  const requestedGlobalLocationForRef = useRef<unknown>(null);

  useEffect(() => {
    if (!locations || globalLocation) return;

    // describeAllLocations / describeLocationWorkflow accept the same scopes as
    // the locations list; without this gate a limited role 403s and surfaces a
    // "Missing permission" toast on every visitor page.
    if (!hasLocationRead) return;

    // If userId is missing, user locations cannot be fetched; fall back to
    // org locations instead of waiting forever.
    if (hasUserReadWrite && userId && !selectedUserLocations) {
      return;
    }

    if (requestedGlobalLocationForRef.current === locations) return;

    const targetLocation =
      selectedUserLocations?.length > 0
        ? selectedUserLocations[0]
        : locations?.[0];

    if (targetLocation?.id) {
      requestedGlobalLocationForRef.current = locations;
      dispatch(
        describeLocation({
          orgId,
          locationId: targetLocation.id,
          global: true,
        }),
      );
      dispatch(
        describeLocationWorkflow({
          orgId,
          locationId: targetLocation.id,
          global: true,
        }),
      );
    }
  }, [
    dispatch,
    orgId,
    userId,
    hasUserReadWrite,
    hasLocationRead,
    locations,
    globalLocation,
    selectedUserLocations,
  ]);

  if (isLoading) {
    return <Loader content="Loading Visitor..." />;
  }

  // If user doesn't have visitor access, skip loading and let OpScopeGuard show NotFoundPage
  // This matches mailroom's behavior where pages are protected by OpScopeGuard
  if (!hasAnyVisitorScope) {
    return <Outlet />;
  }

  // Only block on location load for users who can actually read locations, and
  // only until their fetch has settled. This prevents an infinite spinner for
  // limited-scope roles (whose locations request 403s) while still waiting for
  // the global location on the initial load for users who can read it.
  if (hasLocationRead && !globalLocation && !locationsFetchDone) {
    return <Loader content="Loading Visitor..." />;
  }
  return <Outlet />;
};
